/**
 * useTaskFiltering - Centralized Task Filtering Hook
 *
 * Single source of truth for task filtering across all views (Feed, Kanban, List).
 *
 * KEY ARCHITECTURE: Server (database) is the source of truth for what tasks exist.
 * The local `tasks` Map only ENRICHES server data with real-time state (streaming,
 * pending questions, etc.). This prevents batch loading where cached tasks appear
 * before server responds.
 *
 * Data flow:
 * 1. serverTasks (from DB) → defines what tasks exist
 * 2. tasks Map (live state) → enriches with real-time info (isStreaming, pendingInteraction)
 * 3. Exception: actively streaming tasks not yet persisted are included
 *
 * Categories: 'queued' | 'active' | 'attention' | 'completed'
 * - queued: No messages yet, waiting to start (includes scheduled tasks)
 * - active: Currently streaming/processing (requires live state)
 * - attention: Needs user input (question, error, auth)
 * - completed: Has messages but not actively processing
 *
 * Status filter mapping:
 * - 'all' → all categories
 * - 'queued' → queued only (Up Next)
 * - 'active' → queued + active (not finished)
 * - 'attention' → attention only
 * - 'completed' → completed only
 *
 * Lifecycle State Persistence:
 * - Server persists lifecycle_state + error_message + error_code in database
 *   (Phase 9: pending_details JSONB column removed; flat scalar columns added)
 * - Server's lifecycleState is authoritative for persistent states (awaiting, error)
 * - Live streaming/processing state overrides server state
 */

import { useMemo } from "react";
import type { OriginChannel } from "@bash/shared";
import type {
  TaskState,
  PersistedTask,
  PersistedSession,
} from "@/stores/types";
import type { PendingInteractionRow } from "@/hooks/useInteractionsRealtime";
import { getIntegrationDisplayName } from "@/lib/composio-tools";
import { handsAttentionPreview } from "@/components/message-input/interactions/handsApproval";
import { permissionHeadline, planUsedUpLine } from "@bash/shared";
import { bashStepLabel, plainStepLabel } from "@/lib/narration/stepLabel";

// =============================================================================
// Types
// =============================================================================

export type TaskCategory = "queued" | "active" | "attention" | "completed";

/** Single source of truth for category display metadata. All views consume this. */
export type CategoryIcon = "clock" | "zap" | "bell" | "check" | "alert";
export const CATEGORY_META: Record<
  TaskCategory,
  { label: string; accent: string; icon: CategoryIcon; gradient: string }
> = {
  queued: {
    label: "Up Next",
    accent: "var(--color-amber)",
    icon: "clock",
    gradient: "var(--bento-amber)",
  },
  active: {
    label: "Working",
    accent: "var(--color-blue)",
    icon: "zap",
    gradient: "var(--bento-sky)",
  },
  attention: {
    label: "Needs Input",
    accent: "var(--color-red)",
    icon: "bell",
    gradient: "var(--bento-rose)",
  },
  completed: {
    label: "Completed",
    accent: "var(--color-green)",
    icon: "check",
    gradient: "var(--bento-cool)",
  },
};

export interface UnifiedTask {
  id: string;
  title: string;
  category: TaskCategory;
  agentSlug: string;
  createdAt: Date;
  lastActivityAt: Date;
  isLive: boolean;
  messageCount: number;
  /** Rich data for attention items */
  attention?: { reason: "question" | "error" | "auth"; preview: string };
  /** Active tool being used */
  tool?: { label: string; icon: string };
  /** Whether a sub-agent (Task tool) is running */
  hasSubagent?: boolean;
  /** Epoch ms when current agent turn started (null if not processing) */
  turnStartTime?: number | null;
  /** Last text preview from completed messages */
  lastMessagePreview?: string;
  /** Error message if status is error */
  errorMessage?: string;
  /** Question preview if pending question */
  questionPreview?: string;
  /** Integration name if auth required */
  authRequired?: string;
  /**
   * Interaction id for the pending attention item, when known. Sourced from the
   * realtime interactions feed (interactionsByTaskId) or the live task's
   * pendingInteraction. Lets the attention card resolve a NON-selected task by
   * id (resolveInteractionApi) without depending on which task is open.
   */
  interactionId?: string;
  /** If this task was created by a routine */
  routineId?: string;
  /** Phase 6 Branch — set when this task was branched from another. Drives the ⑂ badge. */
  parentTaskId?: string | null;
  /** R-A — the door this task was born through. Drives the channel mark on the
   *  list row; absent = app-born, which is every task created inside Vestra. */
  originChannel?: OriginChannel;
}

export interface TaskFilters {
  search?: string;
  dateFilter?: "today" | "week" | "month" | "all";
  statusFilter?: "all" | "queued" | "active" | "completed" | "attention";
}

export interface FilteredTaskResult {
  /** All filtered tasks */
  all: UnifiedTask[];
  /** Tasks by category */
  byCategory: {
    queued: UnifiedTask[];
    active: UnifiedTask[];
    attention: UnifiedTask[];
    completed: UnifiedTask[];
  };
  /** Stats computed from filtered tasks - always in sync with what's displayed */
  stats: {
    queued: number;
    active: number;
    attention: number;
    completed: number;
    total: number;
  };
}

export interface UseTaskFilteringProps {
  /** Live tasks from socket */
  tasks: Map<string, TaskState>;
  /** Server persisted tasks */
  serverTasks?: PersistedTask[];
  /** Session history fallback */
  sessionHistory?: PersistedSession[];
  /**
   * Realtime pending-interaction rows keyed by task_id (last-write-wins). The
   * ONLY realtime carrier of interaction kind/toolkit/displayName for tasks with
   * no live state (cross-tab / live-stateless pauses) — the server's TASK_STREAM
   * deliberately omits pendingDetails. Folded in as the highest-priority
   * attention-enrichment source so the beautiful Connect card renders instantly
   * for every paused task, GATED on the task already classifying as 'attention'
   * (a stray row never promotes a non-awaiting task).
   */
  interactionsByTaskId?: Map<string, PendingInteractionRow>;
  /**
   * Number of pending interaction rows per task (ask queue). When a task has
   * several parallel asks, the attention card must say how many ("3 approvals
   * needed") instead of implying a single one. Absent or 1 ⇒ per-ask preview.
   */
  interactionCountsByTaskId?: Map<string, number>;
  /** Filters to apply */
  filters: TaskFilters;
}

// =============================================================================
// Tool Display Helpers
// =============================================================================

const TOOL_ICONS: Record<string, string> = {
  Read: "📖",
  Write: "✏️",
  Edit: "✏️",
  NotebookEdit: "✏️",
  Bash: "💻",
  Glob: "🔍",
  Grep: "🔍",
  WebSearch: "🌐",
  WebFetch: "🌐",
  Task: "🚀",
  AskUserQuestion: "💬",
};

const TOOL_LABELS: Record<string, string> = {
  Read: "Reading file",
  Write: "Writing file",
  Edit: "Editing file",
  Glob: "Searching files",
  Grep: "Searching code",
  WebSearch: "Searching web",
  WebFetch: "Fetching page",
  Task: "Running sub-agent",
  TodoWrite: "Updating todos",
  TaskCreate: "Adding todo",
  TaskUpdate: "Updating todo",
  TaskGet: "Reading todo",
  TaskList: "Reviewing todos",
  TodoCreate: "Adding todo",
  TodoUpdate: "Updating todo",
  TodoGet: "Reading todo",
  TodoList: "Reviewing todos",
  AskUserQuestion: "Asking question",
  NotebookEdit: "Editing notebook",
};

// =============================================================================
// Classification Helpers
// =============================================================================

/**
 * Classify a live task into a category.
 * Only live tasks can be 'active' (streaming/processing).
 *
 * `pendingInteraction` is the single source of truth for "needs attention".
 * Question blocks and AskUserQuestion tool_use blocks in `streamingMessage`
 * are always created in lockstep with `pendingInteraction` (see
 * stores/handlers/interactionHandlers.ts), so scanning streamingMessage as
 * a fallback only ever surfaces stale state from leak paths — and
 * misclassifies non-question kinds (auth, plan, credit, ...) as questions.
 */
export function categorizeLiveTask(task: TaskState): TaskCategory {
  // Attention states - highest priority
  if (task.pendingInteraction) return "attention";

  // Active states - currently processing or in transient in-flight phases
  if (
    task.phase === "processing" ||
    task.phase === "waiting" ||
    task.phase === "interrupting"
  )
    return "active";

  // Completed - has messages but not processing
  if (task.messages.length > 0) return "completed";

  // Queued - no messages yet
  return "queued";
}

/**
 * Classify a server task using persisted lifecycle state.
 *
 * Lifecycle state mapping:
 * - 'idle' → 'completed' (not actively processing)
 * - 'processing' → 'active' (but we likely won't see this from server - live task takes precedence)
 * - 'awaiting' → 'attention' (user input needed - auth, question, approval)
 * - 'error' → 'attention' (error requiring attention)
 * - 'completed' → 'completed'
 *
 * Falls back to 'completed' if no lifecycle state (legacy tasks).
 */
function categorizeServerTask(task: PersistedTask): TaskCategory {
  // Spec 2026-09-12 — a teammate of this task is waiting on the person, so the task is
  // waiting on the person, whatever the LEAD itself is doing. Above the lifecycle read on
  // purpose: the common case is a lead that finished its turn and reads `idle`, which
  // would otherwise bucket as completed while its teammate sits on an unanswered question.
  if (task.laneNeedsYou) return "attention";

  // Use persisted lifecycle state if available
  if (task.lifecycleState) {
    switch (task.lifecycleState) {
      case "scheduled":
      case "queued":
        return "queued";
      case "awaiting":
      case "error":
        return "attention";
      case "processing":
        // Technically could happen if server persisted but client doesn't have live state
        return "active";
      case "completed":
      case "idle":
      default:
        return "completed";
    }
  }

  // Legacy fallback: use status field
  if (task.status === "error") return "attention";

  // Server 'active' = session open, but agent is idle (not processing)
  // Without live state, we can't know if it's truly active → completed
  return "completed";
}

/**
 * Strip <!--routine-preview:JSON--> or <!--agent-preview:JSON--> or <!--skill-preview:JSON-->
 * metadata prefix from question preview text so raw metadata doesn't leak into the UI.
 */
function stripPreviewMetadata(text: string | undefined): string | undefined {
  if (!text) return text;
  const stripped = text
    .replace(/^<!--(?:routine|agent|skill)-preview:[\s\S]*?-->/, "")
    .trim();
  return stripped || text;
}

/**
 * Extract attention info from a persisted task's pending details.
 */
function getServerAttentionInfo(task: PersistedTask): UnifiedTask["attention"] {
  if (!task.pendingDetails) return undefined;

  switch (task.pendingDetails.reason) {
    case "auth":
      return {
        reason: "auth",
        preview: `Connect ${getIntegrationDisplayName(
          task.pendingDetails.integration || "service"
        )} to continue`,
      };
    case "question":
      return {
        reason: "question",
        preview:
          stripPreviewMetadata(task.pendingDetails.questionPreview) ||
          task.pendingDetails.questionPreview,
      };
    case "approval":
      return {
        reason: "question",
        preview: "Review the plan",
      };
    case "permission":
      return {
        reason: "question",
        preview: permissionHeadline(task.pendingDetails.summary),
      };
    case "error":
      return { reason: "error", preview: task.pendingDetails.message };
    case "custom":
      return {
        reason: "question",
        preview: task.pendingDetails.title || "Input needed",
      };
    case "credit":
      return {
        reason: "question",
        preview: `Approve cost: $${task.pendingDetails.estimatedCostUsd.toFixed(
          2
        )}`,
      };
    case "hands":
      // OS Hands P2.5a — same glanceable grammar as the card (handsApproval.ts).
      return {
        reason: "question",
        preview: handsAttentionPreview(task.pendingDetails),
      };
    case "plan_limit": // engines T5: a pending decision, like a credit approval
      return {
        reason: "question",
        preview: planUsedUpLine(task.pendingDetails.request.engine),
      };
    default:
      return undefined;
  }
}

/**
 * Extract attention info from a realtime pending-interaction row.
 *
 * This is the third attention source (after live `pendingInteraction` and server
 * `pendingDetails`) and the only one that carries interaction detail for tasks
 * with no live state — the cross-tab / live-stateless pauses the realtime
 * interactions feed used to own. Maps `row.request` (the discriminated
 * InteractionRequest union) → reason/preview/authRequired, reusing
 * `getIntegrationDisplayName` for the auth preview so it matches `getAttentionInfo`
 * verbatim. Always returns the row id as `interactionId` so the attention card can
 * resolve a non-selected task by id.
 *
 * Every branch below reads `row.request`, so its one caller gates on that column
 * being present: a row whose payload arrived without it is skipped, not passed here.
 */
function getInteractionAttentionInfo(
  row: PendingInteractionRow,
  pendingCount = 1
): {
  attention: UnifiedTask["attention"];
  authRequired?: string;
  interactionId: string;
} {
  const req = row.request;
  if (req.kind === "composio_auth") {
    return {
      attention: {
        reason: "auth",
        preview: `Connect ${getIntegrationDisplayName(
          req.toolkit
        )} to continue`,
      },
      authRequired: req.toolkit,
      interactionId: row.id,
    };
  }
  // Ask queue: several rows pend for this task — say how many (the row map
  // carries only the oldest; the count keeps the card honest cross-tab).
  if (pendingCount > 1) {
    return {
      attention: {
        reason: "question",
        preview: `${pendingCount} approvals needed`,
      },
      interactionId: row.id,
    };
  }
  if (req.kind === "ask_question") {
    const q = req.questions?.[0]?.question;
    return {
      attention: { reason: "question", preview: q || "Quick question" },
      interactionId: row.id,
    };
  }
  if (req.kind === "tool_permission") {
    return {
      attention: {
        reason: "question",
        preview: permissionHeadline(req.summary),
      },
      interactionId: row.id,
    };
  }
  if (req.kind === "credit_approval") {
    return {
      attention: {
        reason: "question",
        preview: `Approve cost: $${req.estimatedCostUsd.toFixed(2)}`,
      },
      interactionId: row.id,
    };
  }
  if (req.kind === "exit_plan") {
    return {
      attention: { reason: "question", preview: "Plan ready for review" },
      interactionId: row.id,
    };
  }
  if (req.kind === "custom") {
    return {
      attention: { reason: "question", preview: req.title || "Input needed" },
      interactionId: row.id,
    };
  }
  if (req.kind === "hands_approval") {
    // OS Hands P2.5a — glanceable OS/browser consent line ("Mail is asking to
    // work"), same grammar as the card so the two never disagree.
    return {
      attention: { reason: "question", preview: handsAttentionPreview(req) },
      interactionId: row.id,
    };
  }
  if (req.kind === "plan_limit") {
    // Engines T5: a pending decision, like a credit approval.
    return {
      attention: { reason: "question", preview: planUsedUpLine(req.engine) },
      interactionId: row.id,
    };
  }
  return {
    attention: { reason: "question", preview: "Input needed" },
    interactionId: row.id,
  };
}

/**
 * Extract attention info from a live task.
 *
 * Reads exclusively from `pendingInteraction`. The streamingMessage scanner
 * fallback was removed deliberately: question blocks are pushed into
 * streamingMessage in the same atomic mutation that sets `pendingInteraction`,
 * so any divergence between the two represents a leak (stale state) that
 * would misclassify the task. Trust `pi`.
 */
function getAttentionInfo(task: TaskState): UnifiedTask["attention"] {
  const attention = getAttentionInfoBase(task);
  // Ask queue (incident a671cd77): several asks pend at once — the card must
  // say how many, never imply one. Auth/error previews keep their own copy.
  const total = 1 + (task.pendingQueue?.length ?? 0);
  if (attention?.reason === "question" && total > 1) {
    return { ...attention, preview: `${total} approvals needed` };
  }
  return attention;
}

function getAttentionInfoBase(task: TaskState): UnifiedTask["attention"] {
  const pi = task.pendingInteraction;
  if (pi?.type === "auth") {
    return {
      reason: "auth",
      preview: `Connect ${getIntegrationDisplayName(pi.toolkit)} to continue`,
    };
  }
  if (task.error) {
    const msg =
      task.error ||
      (
        task.streamingMessage?.content.find((b) => b.type === "error") as
          { error?: string } | undefined
      )?.error ||
      "An error occurred";
    return { reason: "error", preview: msg };
  }
  if (pi?.type === "question") {
    const q = pi.questions?.[0]?.question;
    if (q) return { reason: "question", preview: q };
  }
  if (pi?.type === "approval") {
    return { reason: "question", preview: "Review the plan" };
  }
  if (pi?.type === "permission") {
    return { reason: "question", preview: permissionHeadline(pi.summary) };
  }
  if (pi?.type === "plan") {
    return { reason: "question", preview: "Plan ready for review" };
  }
  if (pi?.type === "credit") {
    return {
      reason: "question",
      preview: `Approve cost: $${pi.estimatedCostUsd.toFixed(2)}`,
    };
  }
  if (pi?.type === "custom") {
    return { reason: "question", preview: pi.title || "Input needed" };
  }
  if (pi?.type === "hands") {
    return { reason: "question", preview: handsAttentionPreview(pi) };
  }
  if (pi?.type === "plan_limit") {
    // Engines T5: the same line the other two sources lead with.
    return { reason: "question", preview: planUsedUpLine(pi.request.engine) };
  }
  return undefined;
}

/**
 * Get current tool display for an active task.
 */
function getToolDisplay(task: TaskState): UnifiedTask["tool"] {
  if (!task.streamingMessage) return undefined;
  const block = task.streamingMessage.content.find(
    (b) =>
      b.type === "tool_use" && (b as { status?: string }).status === "running"
  ) as
    | {
        toolName: string;
        toolInput?: Record<string, unknown>;
        summary?: { action: string; target: string };
        display?: { displayText?: string; humanLabel?: string };
      }
    | undefined;
  if (!block) return undefined;

  const name = block.toolName || "";

  // Use summary if available
  if (block.summary) {
    return {
      label: `${block.summary.action} ${block.summary.target}`,
      icon: TOOL_ICONS[name] || "⚡",
    };
  }

  // Use display data if enriched by server
  if (block.display?.humanLabel || block.display?.displayText) {
    return {
      label: block.display.humanLabel || block.display.displayText || name,
      icon: TOOL_ICONS[name] || "⚡",
    };
  }

  // A command says what it is doing in its own description, and the coding
  // engine's housekeeping tools in plain words: the same words the story's
  // step reads (lib/narration/stepLabel).
  const label =
    (name === "Bash" ? bashStepLabel(block.toolInput, false) : null) ||
    plainStepLabel(name, false) ||
    TOOL_LABELS[name] ||
    name
      .replace(/^mcp__[^_]+__/, "")
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase());

  return { label, icon: TOOL_ICONS[name] || "⚡" };
}

/**
 * Check if task has a sub-agent running.
 */
function hasSubagent(task: TaskState): boolean {
  if (!task.streamingMessage) return false;
  return task.streamingMessage.content.some((b) => {
    if (b.type !== "tool_use") return false;
    const tn = (b as { toolName?: string }).toolName || "";
    return tn === "Task" || tn.startsWith("mcp__");
  });
}

/**
 * Compact signature of every LIVE-task field the dashboard actually renders.
 *
 * The home page subscribes to an array of these (one per task, via useShallow)
 * so it re-renders ONLY when display-relevant state changes — never on the
 * ~60fps streaming-token churn that rebuilds the tasks Map on every delta flush.
 * It folds in the DERIVED running-tool label + hasSubagent (the only
 * streaming-sourced values a card shows) and deliberately excludes raw streaming
 * text. Must stay in lockstep with the UnifiedTask construction below — if a new
 * live field starts driving the UI, add it here too or the card will go stale.
 */
export function taskLiveSignature(task: TaskState): string {
  const pi = task.pendingInteraction as {
    type?: string;
    toolkit?: string;
    title?: string;
    questions?: { question?: string }[];
  } | null;
  const tool = getToolDisplay(task);
  return [
    task.phase,
    task.title ?? "",
    task.agentSlug ?? "",
    task.messages.length,
    task.error ?? "",
    task.turnStartTime ?? "",
    task.parentTaskId ?? "",
    pi
      ? `${pi.type ?? ""}|${pi.toolkit ?? ""}|${
          pi.questions?.[0]?.question ?? ""
        }|${pi.title ?? ""}`
      : "",
    tool?.label ?? "",
    hasSubagent(task) ? "1" : "0",
  ].join("");
}

/**
 * Get last text preview from completed messages.
 */
function getLastMessagePreview(task: TaskState): string | undefined {
  for (let i = task.messages.length - 1; i >= 0; i--) {
    const msg = task.messages[i];
    if (msg.role !== "assistant") continue;
    for (let j = msg.content.length - 1; j >= 0; j--) {
      const block = msg.content[j];
      if (block.type === "text" && (block as { text: string }).text.trim()) {
        return (block as { text: string }).text.trim();
      }
    }
  }
  return undefined;
}

/**
 * Get error message from task.
 */
function getErrorMessage(task: TaskState): string | undefined {
  if (task.error) return task.error;
  if (task.streamingMessage) {
    for (const block of task.streamingMessage.content) {
      if (block.type === "error") return (block as { error: string }).error;
    }
  }
  for (let i = task.messages.length - 1; i >= 0; i--) {
    for (const block of task.messages[i].content) {
      if (block.type === "error") return (block as { error: string }).error;
    }
  }
  return undefined;
}

/**
 * Get question preview from pending question.
 *
 * Reads only from `pendingInteraction` — the streamingMessage scanner
 * fallback was removed for the same reason as `getAttentionInfo`.
 */
function getQuestionPreview(task: TaskState): string | undefined {
  if (
    task.pendingInteraction?.type === "question" &&
    task.pendingInteraction.questions?.[0]?.question
  ) {
    return task.pendingInteraction.questions[0].question;
  }
  return undefined;
}

// =============================================================================
// Date Filter
// =============================================================================

function isWithinDateFilter(
  date: Date,
  filter: "today" | "week" | "month" | "all"
): boolean {
  if (filter === "all") return true;
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (filter === "today") return date >= startOfDay;
  if (filter === "week") {
    const weekAgo = new Date(startOfDay);
    weekAgo.setDate(weekAgo.getDate() - 7);
    return date >= weekAgo;
  }
  if (filter === "month") {
    const monthAgo = new Date(startOfDay);
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    return date >= monthAgo;
  }
  return true;
}

// =============================================================================
// Status Filter Mapping
// =============================================================================

/**
 * Maps UI status filter to which categories to show.
 *
 * | UI Filter     | Shows Categories        |
 * |---------------|-------------------------|
 * | 'all'         | all categories          |
 * | 'queued'      | queued only             |
 * | 'active'      | queued + active         |
 * | 'attention'   | attention only          |
 * | 'completed'   | completed only          |
 */
function matchesStatusFilter(
  category: TaskCategory,
  filter: "all" | "queued" | "active" | "completed" | "attention"
): boolean {
  if (filter === "all") return true;
  if (filter === "queued") return category === "queued";
  if (filter === "active")
    return category === "queued" || category === "active";
  if (filter === "attention") return category === "attention";
  if (filter === "completed") return category === "completed";
  return true;
}

// =============================================================================
// Main Hook
// =============================================================================

export function useTaskFiltering({
  tasks,
  serverTasks = [],
  sessionHistory = [],
  interactionsByTaskId,
  interactionCountsByTaskId,
  filters,
}: UseTaskFilteringProps): FilteredTaskResult {
  return useMemo(() => {
    const { search = "", dateFilter = "all", statusFilter = "all" } = filters;
    const query = search.toLowerCase().trim();

    const all: UnifiedTask[] = [];
    const seen = new Set<string>();

    // ==========================================================================
    // SERVER IS THE SOURCE OF TRUTH for what tasks exist.
    // Live tasks Map only ENRICHES server data with real-time state.
    // ==========================================================================

    // 1. Process server tasks first - they define what tasks exist
    for (const serverTask of serverTasks) {
      seen.add(serverTask.id);

      // Check if we have live state to enrich this task
      const liveTask = tasks.get(serverTask.id);

      // Realtime interaction row for this task (cross-tab / live-stateless
      // carrier of kind/toolkit). Only enriches the 'attention' category —
      // never promotes a non-awaiting task (see the per-branch gates below).
      const interactionRow = interactionsByTaskId?.get(serverTask.id);
      // A row with no `request` enriches nothing. Realtime omits an unchanged
      // TOASTed jsonb column from an UPDATE payload, and everything below is
      // derived from that column, so a bare row is treated exactly like no row
      // at all rather than taking the whole list down (staging, 2026-09-20).
      const interactionAttn = interactionRow?.request
        ? getInteractionAttentionInfo(
            interactionRow,
            interactionCountsByTaskId?.get(serverTask.id) ?? 1
          )
        : undefined;

      if (liveTask) {
        // Enrich server task with live state
        const isActivelyProcessing = liveTask.phase === "processing";

        // For actively streaming/processing tasks, use live state for category
        // For idle tasks, server's lifecycleState is authoritative
        const serverOverridesCategory =
          !isActivelyProcessing &&
          serverTask.lifecycleState &&
          (serverTask.lifecycleState === "awaiting" ||
            serverTask.lifecycleState === "error");

        const category = serverOverridesCategory
          ? categorizeServerTask(serverTask)
          : categorizeLiveTask(liveTask);

        // Use server's attention info if server state takes precedence, otherwise use live task's
        const attentionFromServer =
          serverOverridesCategory && category === "attention"
            ? getServerAttentionInfo(serverTask)
            : undefined;
        const attentionFromLive =
          !serverOverridesCategory && category === "attention"
            ? getAttentionInfo(liveTask)
            : undefined;

        // ORIGINAL (no-row) values — kept byte-identical so the existing
        // Claude/prod path is unchanged when there's no realtime interaction row.
        const baseAttention = attentionFromServer || attentionFromLive;
        const baseAuthRequired =
          serverOverridesCategory &&
          serverTask.pendingDetails?.reason === "auth"
            ? serverTask.pendingDetails.integration
            : liveTask.pendingInteraction?.type === "auth"
              ? liveTask.pendingInteraction.toolkit
              : undefined;

        // Realtime row enrichment — ONLY when the task is already 'attention'
        // (never a promotion) AND a row exists. A same-session live
        // pendingInteraction is the freshest signal and still wins over the row;
        // otherwise the row supplies the detail the server path lacks. When no
        // row is present the chain collapses to the original base* values above.
        const interactionAttnForTask =
          category === "attention" && interactionAttn
            ? interactionAttn
            : undefined;
        const livePiAttention = liveTask.pendingInteraction
          ? getAttentionInfo(liveTask)
          : undefined;
        const livePiAuthRequired =
          liveTask.pendingInteraction?.type === "auth"
            ? liveTask.pendingInteraction.toolkit
            : undefined;

        const resolvedAttention = interactionAttnForTask
          ? (livePiAttention ??
            interactionAttnForTask.attention ??
            baseAttention)
          : baseAttention;
        const resolvedAuthRequired = interactionAttnForTask
          ? (livePiAuthRequired ??
            interactionAttnForTask.authRequired ??
            baseAuthRequired)
          : baseAuthRequired;

        all.push({
          id: serverTask.id,
          title: liveTask.title || serverTask.title || "Task",
          category,
          agentSlug: liveTask.agentSlug || serverTask.agentSlug || "bash",
          createdAt: liveTask.createdAt,
          // Server is canonical for lastActivityAt. Live state (Zustand,
          // persisted to localStorage) can carry stale values from earlier
          // sessions — never trust it for the sort key.
          lastActivityAt: new Date(serverTask.lastActivityAt),
          isLive: isActivelyProcessing,
          messageCount: liveTask.messages.filter((m) => m.role === "assistant")
            .length,
          attention: resolvedAttention,
          tool: category === "active" ? getToolDisplay(liveTask) : undefined,
          hasSubagent: hasSubagent(liveTask),
          turnStartTime: liveTask.turnStartTime,
          lastMessagePreview:
            category === "completed"
              ? getLastMessagePreview(liveTask)
              : undefined,
          errorMessage: serverOverridesCategory
            ? serverTask.errorMessage ||
              (serverTask.pendingDetails?.reason === "error"
                ? serverTask.pendingDetails.message
                : undefined)
            : undefined,
          questionPreview:
            serverOverridesCategory &&
            serverTask.pendingDetails?.reason === "question"
              ? stripPreviewMetadata(serverTask.pendingDetails.questionPreview)
              : category === "attention"
                ? stripPreviewMetadata(getQuestionPreview(liveTask))
                : undefined,
          authRequired: resolvedAuthRequired,
          interactionId:
            liveTask.pendingInteraction?.interactionId ??
            interactionAttnForTask?.interactionId,
          routineId: serverTask.routineId,
          parentTaskId:
            liveTask.parentTaskId ?? serverTask.parentTaskId ?? null,
          // R-A birth channel — immutable, so either copy is the same answer;
          // the live task may simply not have been hydrated with it yet.
          originChannel: liveTask.originChannel ?? serverTask.originChannel,
        });
      } else {
        // Pure server task - no live state available
        const category = categorizeServerTask(serverTask);
        const serverAttentionInfo =
          category === "attention"
            ? getServerAttentionInfo(serverTask)
            : undefined;
        // Realtime row enrichment — the cross-tab / live-stateless carrier of
        // toolkit/kind. Only when already 'attention'. Precedence: interaction ?? server.
        const interactionAttnForTask =
          category === "attention" ? interactionAttn : undefined;
        const serverAuthRequired =
          serverTask.pendingDetails?.reason === "auth"
            ? serverTask.pendingDetails.integration
            : undefined;

        all.push({
          id: serverTask.id,
          title: serverTask.title || "Task",
          category,
          agentSlug: serverTask.agentSlug || "bash",
          createdAt: new Date(serverTask.createdAt),
          lastActivityAt: new Date(serverTask.lastActivityAt),
          isLive: false,
          messageCount: 0,
          attention: interactionAttnForTask?.attention ?? serverAttentionInfo,
          errorMessage:
            serverTask.errorMessage ||
            (serverTask.pendingDetails?.reason === "error"
              ? serverTask.pendingDetails.message
              : undefined),
          questionPreview:
            serverTask.pendingDetails?.reason === "question"
              ? stripPreviewMetadata(serverTask.pendingDetails.questionPreview)
              : undefined,
          authRequired:
            interactionAttnForTask?.authRequired ?? serverAuthRequired,
          interactionId: interactionAttnForTask?.interactionId,
          routineId: serverTask.routineId,
          parentTaskId: serverTask.parentTaskId ?? null,
          originChannel: serverTask.originChannel,
        });
      }
    }

    // 2. Add live tasks ONLY if they're actively being created (not yet persisted)
    //    This handles the case where user just started a new task that hasn't hit the DB yet
    for (const [id, task] of tasks) {
      if (seen.has(id)) continue;

      // P5.4 Agent Teams — hidden teammate lanes are real TaskStates (they can
      // be processing or hold a pendingInteraction) but they render embedded in
      // the parent's TeammateCard, NEVER as list items. Server surfaces already
      // exclude them; this closes the client-minted leak.
      if (task.teammateMeta) continue;

      // Include if actively processing OR needing attention (auth/question)
      // Don't include stale cached tasks - server is source of truth
      const isActivelyProcessing = task.phase === "processing";
      const needsAttention = !!task.pendingInteraction;
      if (!isActivelyProcessing && !needsAttention) continue;

      seen.add(id);

      const category = categorizeLiveTask(task);
      all.push({
        id,
        title: task.title || "New Task",
        category,
        agentSlug: task.agentSlug || "bash",
        createdAt: task.createdAt,
        lastActivityAt: task.lastActivityAt,
        isLive: true,
        messageCount: task.messages.filter((m) => m.role === "assistant")
          .length,
        attention:
          category === "attention" ? getAttentionInfo(task) : undefined,
        tool: category === "active" ? getToolDisplay(task) : undefined,
        hasSubagent: hasSubagent(task),
        turnStartTime: task.turnStartTime,
        lastMessagePreview:
          category === "completed" ? getLastMessagePreview(task) : undefined,
        errorMessage: task.error ? getErrorMessage(task) : undefined,
        questionPreview:
          category === "attention"
            ? stripPreviewMetadata(getQuestionPreview(task))
            : undefined,
        authRequired:
          task.pendingInteraction?.type === "auth"
            ? task.pendingInteraction.toolkit
            : undefined,
        interactionId: task.pendingInteraction?.interactionId,
        parentTaskId: task.parentTaskId ?? null,
        originChannel: task.originChannel,
      });
    }

    // 3. Process session history (fallback for legacy data)
    for (const s of sessionHistory) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);

      all.push({
        id: s.id,
        title: `Task ${s.id.slice(0, 8)}`,
        category: "completed",
        agentSlug: "bash",
        createdAt: new Date(s.createdAt),
        lastActivityAt: new Date(s.updatedAt || s.createdAt),
        isLive: false,
        messageCount: 0,
      });
    }

    // 4. Apply all filters (search + date + status are always AND-combined)
    const filtered = all.filter((t) => {
      if (query && !t.title.toLowerCase().includes(query)) return false;
      if (!isWithinDateFilter(t.lastActivityAt, dateFilter)) return false;
      if (!matchesStatusFilter(t.category, statusFilter)) return false;
      return true;
    });

    // 5. Sort by most recent activity
    const sortByRecent = (a: UnifiedTask, b: UnifiedTask) =>
      b.lastActivityAt.getTime() - a.lastActivityAt.getTime();

    // 6. Partition into categories
    const byCategory = {
      queued: filtered
        .filter((t) => t.category === "queued")
        .sort(sortByRecent),
      active: filtered
        .filter((t) => t.category === "active")
        .sort(sortByRecent),
      attention: filtered
        .filter((t) => t.category === "attention")
        .sort(sortByRecent),
      completed: filtered
        .filter((t) => t.category === "completed")
        .sort(sortByRecent),
    };

    // 7. Compute stats from filtered results (single source of truth!)
    const stats = {
      queued: byCategory.queued.length,
      active: byCategory.active.length,
      attention: byCategory.attention.length,
      completed: byCategory.completed.length,
      total: filtered.length,
    };

    return {
      all: filtered.sort(sortByRecent),
      byCategory,
      stats,
    };
  }, [
    tasks,
    serverTasks,
    sessionHistory,
    interactionsByTaskId,
    interactionCountsByTaskId,
    filters,
  ]);
}
